import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  Address,
  CentPrecisionMoney,
  CustomLineItem,
  LineItem,
} from '@commercetools/platform-sdk';
import { CartInStore } from './types';
import cartMasterService from '../../services/cart/cartMasterService';
import cartService from '../../services/cart';
import catalogService from '../../services/catalog';

import {
  ZIP_ERROR,
  SHIPPING_ADDRESS_UNEXPECTED_ERROR,
} from '../../blocks/Checkout/SteppedCheckout/constants';
import { retryFn } from '../../utils';

import { IS_OUT_OF_STOCK } from '../../utils/common/constants';

const CATALOG_GET_PRODUCT_BY_KEY_RETRY_COUNT = 3;
const CATALOG_GET_PRODUCT_BY_KEY_DELAY_MS = 500;

export const addShippingAddressErrorCode = createAction<string>(
  'addShippingAddressErrorCode',
);

export const setCart = createAction<Partial<CartInStore>>('setCart');

export const cartInStoreInitialValue: CartInStore = {
  id: '',
  version: 0,
  taxMode: '',
  taxRoundingMode: '',
  taxCalculationMode: '',
  inventoryMode: '',
  cartState: '',
  shippingMode: '',

  isDraft: false,
  warrantyPlans: [],
  lineItems: [],
  customLineItems: [],
  previousLineItems: [],
  previousCustomLineItems: [],
  outOfStockLineItems: [],
  productPopularity: [],
  totalPrice: {
    centAmount: 0,
    fractionDigits: 2,
    currencyCode: 'USD',
    type: 'centPrecision',
  },
  status: 'idle',
  showOutOfStockToaster: false,
  recentlyAddedItems: [],
  discountCodeForAbandonedCarts: {},
  discountCodeForAbandonedCheckout: {},
  promoBanner: {
    currentQualifiedSavings: {},
    estimatedQualifiedSavings: {},
    currentTier: 0,
    remainingAmount: 0,
  },
  guaranteeItems: {},
  giftCards: [],
  shipping: [],
  itemShippingAddresses: [],
  discountCodes: [],
  directDiscounts: [],
  refusedGifts: [],
  origin: '',
  createdAt: '',
  lastModifiedAt: '',
  taxedPrice: undefined,
  estimatedTaxAmount: undefined,
  billingAddress: undefined,
  priceRoundingMode: '',
};

const getAddressErrorCodeFromCode = (code?: string) => {
  switch (code) {
    case '502':
    case '500':
    case '':
    case undefined:
      return SHIPPING_ADDRESS_UNEXPECTED_ERROR;
    default:
      return code;
  }
};

export const getTaxEstimation = createAsyncThunk(
  'cart/setEstimatedTax',
  async ({ zipCode }: { zipCode: string }, { getState }) => {
    const { id, isDraft, taxedPrice, totalPrice } = (
      getState() as { cart: CartInStore }
    ).cart;

    const fallbackTaxAmount: CentPrecisionMoney =
      taxedPrice?.totalTax ?? {
        type: 'centPrecision',
        currencyCode: totalPrice.currencyCode,
        centAmount: 0,
        fractionDigits: totalPrice.fractionDigits,
      };

    try {
      const updatedCart = (await cartMasterService.callToMethod({
        cartId: id,
        isDraft,
        methodName: cartMasterService.methodsMap.setShippingAddress,
        paramsObject: {
          shippingAddress: {
            country: 'US',
            postalCode: zipCode,
          },
          cartId: id,
        },
      })) as CartInStore;

      const taxPortionsTotal = updatedCart?.taxedPrice?.taxPortions?.reduce(
        (sum: number, portion: { amount: { centAmount: number } }) =>
          sum + portion.amount.centAmount,
        0,
      );

      const taxAmount: CentPrecisionMoney =
        taxPortionsTotal !== undefined
          ? {
              type: 'centPrecision',
              currencyCode:
                updatedCart?.taxedPrice?.taxPortions?.[0]?.amount
                  ?.currencyCode ?? totalPrice.currencyCode,
              centAmount: taxPortionsTotal,
              fractionDigits: totalPrice.fractionDigits,
            }
          : fallbackTaxAmount;

      return { taxAmount, zipCode };
    } catch (error) {
      // maybe return with a default value instead of an error to avoid blocking the user
      // eslint-disable-next-line no-console
      console.error(
        'Error fetching tax estimation:',
        JSON.stringify({
          errorCode:
            (error as { metadata?: { error_code?: string } })?.metadata
              ?.error_code || (error as { message?: string }).message,
        }),
      );
      return {
        taxAmount: fallbackTaxAmount,
        zipCode,
      };
    }
  },
);

export const setShippingAddressSteppedCheckout = createAsyncThunk<
  CartInStore,
  { shippingAddress: Address },
  { rejectValue: { shippingAddressErrorCode: string } }
>(
  'cart/setShippingAddressSteppedCheckout',
  async ({ shippingAddress }, { getState, rejectWithValue }) => {
    const { id, isDraft } = (getState() as { cart: CartInStore }).cart;

    try {
      return (await cartMasterService.callToMethod({
        cartId: id,
        isDraft,
        methodName: cartMasterService.methodsMap.setShippingAddress,
        paramsObject: { shippingAddress, cartId: id },
      })) as CartInStore;
    } catch (error) {
      const typedError = error as {
        apiErrors?: { title?: string; code: string }[];
      };
      const errorCode = typedError.apiErrors?.some((apiError) =>
        apiError?.title ? apiError?.title?.indexOf?.('Zip') > 0 : false,
      )
        ? ZIP_ERROR
        : getAddressErrorCodeFromCode(typedError.apiErrors?.[0]?.code);
      return rejectWithValue({
        shippingAddressErrorCode: errorCode,
      });
    }
  },
);

export const setBillingAddress = createAsyncThunk(
  'cart/setBillingAddress',
  async (
    { billingAddress }: { billingAddress: Address },
    { getState, rejectWithValue },
  ) => {
    const { id, isDraft } = (getState() as { cart: CartInStore }).cart;

    try {
      return (await cartMasterService.callToMethod({
        cartId: id,
        isDraft,
        methodName: cartMasterService.methodsMap.setBillingAddress,
        paramsObject: { billingAddress, cartId: id },
      })) as Address;
    } catch (error) {
      const typedError = error as {
        apiErrors?: { title?: string; code: string }[];
      };

      // eslint-disable-next-line no-console
      console.error(
        `CheckoutError: setBillingAddress - ${JSON.stringify(error)}`,
      );
      return rejectWithValue({
        billingAddressErrorCode: getAddressErrorCodeFromCode(
          typedError?.apiErrors?.[0]?.code,
        ),
      });
    }
  },
);

export const addPromoCode = createAsyncThunk(
  'cart/addPromoCode',
  async (promoCode: string, { getState, rejectWithValue }) => {
    const { id } = (getState() as { cart: CartInStore }).cart;
    try {
      return await cartService.addPromoCode({ promoCode, cartId: id });
    } catch (error) {
      const typedError = error as {
        metadata?: { error_code?: string };
        message?: string;
      };
      return rejectWithValue({
        errorCode: typedError.metadata?.error_code || typedError.message,
      });
    }
  },
);

export const removePromoCode = createAsyncThunk(
  'cart/removePromoCode',
  (promoCode: string, { getState }) => {
    const { id } = (getState() as { cart: CartInStore }).cart;
    return cartService.removePromoCode({ promoCode, cartId: id });
  },
);

export const getAvailableDiscountCodeForAbandonedCheckout = createAsyncThunk(
  'cart/getAvailableDiscountCodeForAbandonedCheckout',
  (params: string) =>
    cartService.getAvailableDiscountCodeForAbandonedCheckout(params),
);

const convertPreviousLineItem = (lineItem: LineItem) => ({
  id: lineItem.id,
  quantity: lineItem.quantity,
  price: lineItem.price.value,
  custom: lineItem.custom,
  variant: {
    sku: lineItem.variant.sku,
    images: lineItem.variant.images,
    attributes:
      lineItem.variant?.attributes?.filter(
        (attribute) =>
          attribute.name === 'productUrl' || attribute.name === 'title',
      ) ?? lineItem.variant.attributes,
  },
});

const convertPreviousCustomLineItem = (lineItem: CustomLineItem) => ({
  id: lineItem.id,
  quantity: lineItem.quantity,
  custom: lineItem.custom,
  totalPrice: lineItem.totalPrice,
  name: lineItem.name,
});

export const addLineItems = createAsyncThunk(
  'cart/addLineItems',
  async (
    {
      lineItems,
    }: {
      lineItems: {
        sku: string;
        quantity: number;
        item_list_id?: string;
      }[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      let oldCart = (getState() as { cart: CartInStore }).cart;

      if (!oldCart.id) {
        try {
          // Resolves to null when the user has no cart yet (the service
          // answers 204); keep the empty cart already in the store so the
          // reads below stay safe. A genuine failure still lands in catch.
          oldCart = (await cartService.getActiveCart()) ?? oldCart;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('addLineItems getActiveCart error', error);
        }
      }

      const cartId = oldCart.id;

      const previousLineItems = oldCart
        ? oldCart.lineItems.map(convertPreviousLineItem)
        : [];

      const previousCustomLineItems = oldCart
        ? oldCart.customLineItems.map(convertPreviousCustomLineItem)
        : [];

      const cart = (await cartService.addLineItems({
        lineItems,
        cartId,
      })) as CartInStore;
      return {
        ...cart,
        previousLineItems,
        previousCustomLineItems,
      };
    } catch (error: unknown) {
      const typedError = error as {
        apiErrors?: { code: string }[];
      };
      return rejectWithValue({
        errorCode: typedError?.apiErrors?.[0]?.code,
      });
    }
  },
);

export const setShowOutOfStockToaster = createAction<boolean>(
  'setShowOutOfStockToaster',
);

export const checkLineItemsForOOSVariants = createAsyncThunk(
  'cart/checkLineItemsForOOSVariants',
  async (_, { getState }) => {
    const { lineItems } = (getState() as { cart: CartInStore }).cart;
    const productKeysOnCart = Array.from(
      new Set(lineItems?.map((item) => item.productKey) ?? []),
    );

    const oosVariantsForProductsOnCart =
      (
        await Promise.all(
          productKeysOnCart.flatMap(async (productKey) => {
            const productByKeyResponse = (await retryFn({
              fn: catalogService.getProductByKey,
              args: productKey,
              retriesLeft: CATALOG_GET_PRODUCT_BY_KEY_RETRY_COUNT,
              delayTime: CATALOG_GET_PRODUCT_BY_KEY_DELAY_MS,
            })) as {
              variants: {
                sku?: string;
                attributes?: { name: string; value: string | number }[];
              }[];
              masterVariant: {
                sku?: string;
                attributes?: { name: string; value: string | number }[];
              };
            };

            const productOutOfStockVariants = [
              ...(productByKeyResponse?.variants?.filter(
                (variant) =>
                  variant.attributes?.find(
                    (attribute) => attribute.name === IS_OUT_OF_STOCK,
                  )?.value,
              ) ?? []),
              ...(productByKeyResponse.masterVariant?.attributes?.find(
                (attribute) => attribute.name === IS_OUT_OF_STOCK,
              )?.value
                ? [productByKeyResponse.masterVariant]
                : []),
            ];

            return productOutOfStockVariants;
          }),
        )
      )?.flat?.() ?? [];

    const outOfStockLineItems =
      lineItems?.filter((lineItem) =>
        oosVariantsForProductsOnCart.find(
          (oosVariant) => oosVariant.sku === lineItem.variant?.sku,
        ),
      ) ?? [];

    return outOfStockLineItems
      .filter((lineItem) => lineItem.variant?.sku !== undefined)
      .map((lineItem) => ({
        variant: {
          sku: lineItem.variant.sku!,
        },
      }));
  },
);

export const removeLineItem = createAsyncThunk(
  'cart/removeLineItem',
  async ({ lineItemId }: { lineItemId: string }, { getState }) => {
    let oldCart = (getState() as { cart: CartInStore }).cart;

    if (!oldCart.id) {
      try {
        // Resolves to null when the user has no cart yet (the service
        // answers 204); keep the empty cart already in the store so the
        // reads below stay safe. A genuine failure still lands in catch.
        oldCart = (await cartService.getActiveCart()) ?? oldCart;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('removeLineItem getActiveCart error', error);
      }
    }

    const cartId = oldCart.id;
    return cartService.removeLineItem({ lineItemId, cartId });
  },
);
